import React, { useEffect, useState } from "react";
import { ActivityCard, PlayButton, ProgressPill } from "../shared";

/**
 * MissingIsIntro — "The Missing 'Is'" hook for the nominal-sentence lesson.
 *
 * Each row shows an English sentence with its copula (am/is/are) highlighted;
 * tapping the copula strikes it out and reveals the verbless Arabic sentence
 * with audio. Completes when every copula has been tapped away.
 */
export default function MissingIsIntro({ data, onComplete, audio }) {
  const [vanishedIds, setVanishedIds] = useState(() => new Set());

  const items = data.items;
  const allVanished = vanishedIds.size >= items.length;

  useEffect(() => {
    if (allVanished) onComplete();
  }, [allVanished, onComplete]);

  const handleVanish = (item) => {
    if (vanishedIds.has(item.id)) return;
    setVanishedIds((prev) => new Set(prev).add(item.id));
    if (audio) audio.play(item.ar);
  };

  return (
    <ActivityCard>
      <div className="flex items-center justify-end mb-4">
        <ProgressPill>{vanishedIds.size}/{items.length} vanished</ProgressPill>
      </div>

      <div className="flex flex-col gap-3">
        {items.map((item) => {
          const vanished = vanishedIds.has(item.id);
          return (
            <div
              key={item.id}
              className="rounded-xl px-4 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
              style={{ background: "var(--ink)", border: "1px solid var(--panel-line)" }}
            >
              <p className="text-lg m-0" style={{ color: "var(--parchment)" }}>
                {item.en_before}{" "}
                {vanished ? (
                  <span className="line-through opacity-40" style={{ color: "var(--parchment-dim)" }}>
                    {item.copula}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleVanish(item)}
                    className="rounded-md px-1.5 transition-all hover:brightness-125 animate-pulse font-semibold"
                    style={{
                      color: "var(--gold-bright)",
                      background: "var(--gold-bg)",
                      border: "1px dashed var(--gold-dim)",
                      fontSize: "inherit",
                      lineHeight: "inherit",
                    }}
                    title="Tap to see what Arabic does with this word"
                  >
                    {item.copula}
                  </button>
                )}{" "}
                {item.en_after}
              </p>

              {vanished && (
                <p className="m-0 flex items-center gap-3 sm:justify-end">
                  <span dir="rtl" lang="ar" className="font-arabic text-3xl leading-[1.7]" style={{ color: "var(--tag-violet)" }}>
                    {item.ar}
                  </span>
                  {audio && (
                    <PlayButton text={item.ar} playingText={audio.playingText} onPlay={audio.play} />
                  )}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {allVanished && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">✨</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>{data.takeaway}</p>
        </div>
      )}
    </ActivityCard>
  );
}
